import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type {
  FormControl,
  NonNullableFormBuilder,
  ValidatorFn,
} from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxCustomAddress, OnyxOptional, OnyxTime } from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxCheckboxComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxFormGroupComponent,
  OnyxIconComponent,
  OnyxMessageComponent,
  onyxMinDateTimeValidator,
  OnyxNamePipe,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
} from '@onyx/angular';
import { chain, isEqual, omit } from 'lodash';
import { DateTime } from 'luxon';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { SuggestionHelper } from '../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { Unavailability } from '../../../../common/interfaces/common/unavailability';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import { DriverHelper } from '../../../drivers/common/helpers/driver.helper';
import type { Driver } from '../../../drivers/common/interfaces/driver';
import { FleetHelper } from '../../../fleet/common/helpers/fleet.helper';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';
import type { DelegationCrew } from '../../common/services/delegations-form.service';
import { DelegationsFormService } from '../../common/services/delegations-form.service';

type DelegationEndFormGroup = ReturnType<
  (typeof DelegationEndFormComponent)['buildForm']
>;

@Component({
  selector: 'app-delegation-end-form',
  imports: [
    OnyxSectionComponent,
    OnyxTimepickerComponent,
    ReactiveFormsModule,
    OnyxAddressInputComponent,
    OnyxDatepickerComponent,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxMessageComponent,
    TranslatePipe,
    OnyxIconComponent,
    OnyxCheckboxComponent,
  ],
  templateUrl: './delegation-end-form.component.html',
  styleUrl: './delegation-end-form.component.scss',
})
export class DelegationEndFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly translateService = inject(TranslateService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly delegationsFormService = inject(DelegationsFormService);

  public readonly form = input.required<DelegationEndFormGroup>();
  public readonly isReturningControl = input.required<FormControl<boolean>>();
  public readonly hasDailyReturns = input(false);

  protected readonly I18N = 'delegations.delegationPeriodForm';
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
  );

  protected isReturning!: Signal<boolean>;
  protected endDate!: Signal<OnyxOptional<string>>;

  protected delegationCrew = this.delegationsFormService.crew;
  protected driverBase = this.delegationsFormService.driverBase;
  protected driverHome = this.delegationsFormService.driverHome;
  protected vehicleBase = this.delegationsFormService.vehicleBase;

  protected warnings = computed(() =>
    chain([this.fleetWarnings(), this.driversWarnings()])
      .flatMap((warnings) => warnings)
      .compact()
      .value(),
  );

  protected driversUnavailability = computed(
    () => {
      const drivers = omit(this.delegationCrew(), ['vehicle', 'trailer']);
      const startDate = this.delegationsFormService.startDate();
      const endDate = this.endDate();

      if (!drivers || !startDate) return null;

      const isOverlapping = ({ dateRange }: Unavailability) => {
        const from = dateRange.from!;
        const to = dateRange.to!;
        return endDate
          ? to >= startDate && from <= endDate
          : startDate <= to && startDate >= from;
      };

      return chain(['primaryDriver', 'secondaryDriver'] as const)
        .map((crewItem) => {
          const driver = drivers[crewItem];
          if (!driver) return null;

          const overlappingUnavailabilities = [
            ...(driver.unavailability ? [driver.unavailability] : []),
            ...driver.futureUnavailabilities,
          ]
            .filter(Boolean)
            .filter((unavailability) => isOverlapping(unavailability));
          if (!overlappingUnavailabilities.length) return null;

          const formattedUnavailabilities = overlappingUnavailabilities.map(
            (unavailability) =>
              this.datePipe.transform(
                unavailability.dateRange.from!,
                'date-dot',
                { endDate: unavailability.dateRange.to! },
              ),
          );
          return {
            message: 'driverUnavailable',
            data: {
              driver: this.namePipe.transform(driver.driverData),
              unavailabilities: this.joinPipe.transform(
                formattedUnavailabilities,
                { maxLength: 4 },
              ),
            },
          };
        })
        .compact()
        .value();
    },
    { equal: isEqual },
  );

  protected addressSuggestions = computed(() =>
    SuggestionHelper.getDelegationAddressSuggestions(
      this.driverBase(),
      this.driverHome(),
      this.vehicleBase(),
    ),
  );

  private fleetWarnings = computed(() => {
    return this.getWarnings<Fleet>(
      ['vehicle', 'trailer'],
      (fleet) =>
        FleetHelper.getVehicleExpiryDocuments(fleet).map(
          ({ key, expiryDate }) => ({
            expiryDate,
            key: this.translateService.instant(`labels.${key}`),
          }),
        ),
      'fleetDocumentsWillExpire',
      this.fleetIdentifierPipe.transform,
    );
  });

  private driversWarnings = computed(() => {
    return this.getWarnings<Driver>(
      ['primaryDriver', 'secondaryDriver'],
      (driver) => [
        ...DriverHelper.getDriverExpiryDocuments(driver).map(
          ({ key, expirationDate }) => ({
            key: this.translateService.instant(`labels.${key}`),
            expiryDate: expirationDate,
          }),
        ),
        ...driver.driversLicenseAndProfessionalQualifications.categories.map(
          (category) => ({
            key: `${this.translateService.instant(`labels.drivingLicense`)} 
            ${this.translateService.instant(`${DictionaryCode.DRIVERS_LICENSE_CATEGORY}.${category.category}`)}`,
            expiryDate: category.expirationDate?.date,
            isDrivingLicense: true,
          }),
        ),
        ...(driver.employmentConditions.contractTerm.to
          ? [
              {
                key: this.translateService.instant('labels.contract'),
                expiryDate: driver.employmentConditions.contractTerm.to,
              },
            ]
          : []),
      ],
      'driverDocumentsWillExpire',
      (driver) => this.namePipe.transform(driver?.driverData),
    );
  });

  public ngOnInit(): void {
    const controls = this.form().controls;

    this.isReturning = toSignal(this.isReturningControl().valueChanges, {
      initialValue: this.isReturningControl().value,
      injector: this.injector,
    });

    this.endDate = toSignal(controls.arrivalDate.valueChanges, {
      initialValue: controls.arrivalDate.value,
      injector: this.injector,
    });

    effect(
      () => {
        ValidationHelper.toggleRequiredValidator(
          [controls.arrivalDate, controls.arrivalPlace, controls.arrivalTime],
          this.hasDailyReturns() || this.isReturning(),
        );
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const startDate = this.delegationsFormService.startDate();
        const startTime = this.delegationsFormService.startTime();
        const form = this.form();

        if (startDate && startTime) {
          const minDateTime: DateTime = DateTime.fromISO(
            `${startDate}T${startTime}`,
          ).plus({ minute: 1 });

          form.setValidators(
            onyxMinDateTimeValidator(
              minDateTime,
              minDateTime,
              'arrivalDate',
              'arrivalTime',
            ) as ValidatorFn,
          );
        } else {
          form.controls.arrivalDate?.setErrors(null);
          form.controls.arrivalTime?.setErrors(null);
          form.clearValidators();
        }

        form.updateValueAndValidity();
      },
      { injector: this.injector },
    );
  }

  private getWarnings<T>(
    keys: (keyof DelegationCrew)[],
    getDocuments: (value: T) => { key: string; expiryDate?: string | null }[],
    message: string,
    getIdentifier: (value: T) => string,
  ) {
    const delegationCrew = this.delegationCrew();
    if (!delegationCrew || !this.endDate()) return null;

    return chain(keys)
      .map((key) => {
        const value = delegationCrew?.[key] as T;
        if (!value) return null;

        const expiredDocuments = getDocuments(value)
          .filter(
            ({ expiryDate }) => expiryDate && expiryDate < this.endDate()!,
          )
          .map(({ key }) => key);
        if (!expiredDocuments.length) return null;

        return {
          message,
          data: {
            identifier: getIdentifier(value),
            documents: this.joinPipe.transform(expiredDocuments, {
              separator: '/',
              maxLength: 7,
            }),
          },
        };
      })
      .compact()
      .value();
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      arrivalPlace: fb.control<OnyxCustomAddress | null>(null),
      arrivalDate: fb.control<string | null>(null),
      arrivalTime: fb.control<OnyxTime | null>(null),
    });
  }
}
